import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  Inbox,
  PauseCircle,
  Tag,
} from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Rich renderings for typed messages, shared by the agent dashboard and the chat widget:
 *  - ticket:  a query raised from a form/API (subject, category, page context)
 *  - article: a help article shared by an agent
 *  - status:  a status change, shown as a timeline event
 */

type Meta = Record<string, unknown> | null | undefined;
const str = (v: unknown) => (typeof v === 'string' ? v : null);

export function TicketCard({
  meta,
  body,
  accent,
  compact,
}: {
  meta: Meta;
  body: string;
  accent?: string;
  compact?: boolean;
}) {
  const subject = str(meta?.subject);
  const category = str(meta?.category);
  const context = (meta?.context ?? null) as Record<string, string | number | boolean> | null;
  const fields = Array.isArray(meta?.fields)
    ? (meta.fields as { id: string; label: string; value: string | number | boolean }[])
    : [];
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl bg-white text-left shadow-sm ring-1 ring-slate-200/80',
        compact ? 'max-w-[85%]' : 'w-full max-w-md',
      )}
    >
      <div
        className="flex items-center gap-2 px-3.5 py-2 text-[11px] font-semibold tracking-wide text-white uppercase"
        style={{ background: accent ?? 'var(--color-brand-500)' }}
      >
        <Inbox className="h-3.5 w-3.5" aria-hidden /> Query submitted
      </div>
      <div className="space-y-2 px-3.5 py-3">
        {subject && <p className="text-sm font-semibold text-slate-900">{subject}</p>}
        {category && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
            <Tag className="h-3 w-3" aria-hidden /> {category}
          </span>
        )}
        {fields.length > 0 && (
          <dl className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-100">
            {fields.map((f) => (
              <div key={f.id} className="flex gap-3 px-2.5 py-1.5 text-xs">
                <dt className="w-2/5 shrink-0 text-slate-500">{f.label}</dt>
                <dd className="min-w-0 flex-1 font-medium break-words whitespace-pre-wrap text-slate-800">
                  {typeof f.value === 'boolean' ? (f.value ? 'Yes' : 'No') : String(f.value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <p className="text-sm whitespace-pre-wrap text-slate-700">{body}</p>
        {context && Object.keys(context).length > 0 && (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-lg bg-slate-50 p-2 text-[11px]">
            {Object.entries(context).map(([k, v]) => (
              <div key={k} className="min-w-0">
                <dt className="truncate text-slate-400">{k}</dt>
                <dd className="truncate font-medium text-slate-700">{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

export function ArticleCard({
  meta,
  body,
  accent,
  onOpen,
}: {
  meta: Meta;
  body: string;
  accent?: string;
  onOpen?: () => void;
}) {
  const title = str(meta?.title) ?? 'Help article';
  const excerpt = str(meta?.excerpt);
  const note = body && body !== title ? body : null;
  const Wrapper = onOpen ? 'button' : 'div';
  return (
    <div className="w-full max-w-sm space-y-1.5">
      {note && <p className="text-sm whitespace-pre-wrap">{note}</p>}
      <Wrapper
        {...(onOpen ? { type: 'button' as const, onClick: onOpen } : {})}
        className={cn(
          'flex w-full items-start gap-3 rounded-2xl bg-white p-3 text-left shadow-sm ring-1 ring-slate-200/80',
          onOpen && 'transition hover:-translate-y-0.5 hover:shadow-md',
        )}
      >
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
          style={{ background: accent ?? 'var(--color-brand-500)' }}
        >
          <BookOpen className="h-4 w-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold tracking-wide text-slate-400 uppercase">
            Help article
          </span>
          <span className="block text-sm font-semibold text-slate-900">{title}</span>
          {excerpt && <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500">{excerpt}</span>}
        </span>
        {onOpen && <ChevronRight className="mt-3 h-4 w-4 shrink-0 text-slate-300" aria-hidden />}
      </Wrapper>
    </div>
  );
}

const STATUS_ICON: Record<string, typeof CircleDot> = {
  Open: CircleDot,
  Pending: Clock3,
  'On hold': PauseCircle,
  Solved: CheckCircle2,
  Closed: CheckCircle2,
};
const VISITOR_TEXT: Record<string, string> = {
  Open: 'Your query was reopened',
  Pending: 'We replied — waiting for you',
  'On hold': 'We’re checking with the team',
  Solved: 'Marked as resolved',
  Closed: 'Conversation closed',
};

export function StatusEvent({
  status,
  at,
  audience = 'visitor',
}: {
  status: string;
  at?: string;
  audience?: 'visitor' | 'agent';
}) {
  const Icon = STATUS_ICON[status] ?? CircleDot;
  const done = status === 'Solved' || status === 'Closed';
  return (
    <div className="flex items-center gap-3 py-1 text-[11px] font-medium text-slate-400">
      <span className="h-px flex-1 bg-slate-200" />
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1',
          done ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-white text-slate-600 ring-slate-200',
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {audience === 'visitor' ? (VISITOR_TEXT[status] ?? status) : `Status: ${status}`}
        {at && <span className="text-slate-400">· {at}</span>}
      </span>
      <span className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

/** "Sham joined the chat" — a teammate took over the conversation (shown to the visitor). */
export function JoinedEvent({ name, at, accent }: { name: string; at?: string; accent?: string }) {
  return (
    <div className="flex items-center gap-3 py-1 text-[11px] font-medium text-slate-500">
      <span className="h-px flex-1 bg-slate-200" />
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white py-1 pr-2.5 pl-1 ring-1 ring-slate-200">
        <span
          className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
          style={{ background: accent ?? 'var(--color-brand-500)' }}
          aria-hidden
        >
          {name.charAt(0).toUpperCase()}
        </span>
        <span>
          <strong className="font-semibold text-slate-700">{name}</strong> joined the chat
        </span>
        {at && <span className="text-slate-400">· {at}</span>}
      </span>
      <span className="h-px flex-1 bg-slate-200" />
    </div>
  );
}
